import posthog from 'posthog-js';
import type { Path } from './data/types';
import { captureCampaign, loadCampaign } from './lib/campaign';
import { initMetaPixel, metaFromEvent } from './lib/meta-pixel';
import { inAppBrowser } from './lib/in-app-browser';
import { ideaStep, placeStep, landing } from './lib/experiments';
import { trackingAllowed } from './lib/tracking-guard';

// Events are per challenge, never per person (10 §13). Every event carries
// challengeId, day, door and path; the two derived numbers are computed in PostHog.
const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';

/**
 * Session replay, for the rounds of watched feedback.
 *
 * The events say where somebody stopped; only the replay says why, and a
 * feedback weekend cannot be re-run. So this is on wherever there is a PostHog
 * key, and `VITE_POSTHOG_REPLAY=off` turns it off again when the watching is
 * done. It also needs switching on once in the PostHog project itself.
 *
 * **What people type is recorded, on purpose**: a runner with three ideas in it
 * and a forty-second pause is the entire finding, and masking every input would
 * leave a replay of somebody typing into grey boxes. The exceptions are the
 * places the app has promised nobody is reading — Pages and any `input.private`
 * or `pickHidden` answer — which carry `data-private` in the markup and are
 * masked here as well as kept off the card.
 */
const REPLAY = (import.meta.env.VITE_POSTHOG_REPLAY as string | undefined) !== 'off';

let ready = false;
export function initAnalytics() {
  // The campaign is read before the key check: it must be captured on this
  // first landing whether or not analytics is switched on, because the url
  // carrying it is gone a tap later.
  const before = loadCampaign()?.at;
  const c = captureCampaign();
  if (c) setEventBase({ source: c.source, medium: c.medium, campaign: c.campaign, content: c.content, entry: c.door, ...(c.start ? { starter: c.start } : {}) });
  // The experiment arms, likewise before anything is sent and before App.tsx
  // takes `?idea_step=` off the url (lib/experiments.ts): every event carries them.
  // `landing` only for the people index.html gave an arm (the ad's imagine starter with no door).
  const lands = landing();
  const experiments = { idea_step: ideaStep(), place_step: placeStep(), ...(lands ? { landing: lands } : {}) };
  setEventBase(experiments);
  // Dev servers, LAN phones and automated browsers reach neither PostHog nor Meta.
  if (!trackingAllowed()) return;
  // Independent of PostHog: its own id, its own no-op when that is missing.
  initMetaPixel();
  if (!KEY || ready) return;
  posthog.init(KEY, {
    api_host: HOST,
    capture_pageview: false,
    autocapture: false,
    persistence: 'localStorage',
    disable_session_recording: !REPLAY,
    session_recording: { maskAllInputs: false, maskTextSelector: '[data-private], [data-private] *' },
  });
  // On every event: which app's built-in browser this is, if any. Every ad
  // visitor so far was in Facebook's, and that is where Google sign-in and the
  // email link's way back both break (lib/in-app-browser.ts). 'none' rather
  // than null, so a breakdown has a row for real browsers.
  posthog.register({ in_app_browser: inAppBrowser() ?? 'none' });
  ready = true;
  // PostHog's own events (replay, $identify…) carry the arm too, not only ours
  posthog.register(experiments);
  // The top of every funnel. Pageviews are off, so without this the first row
  // anybody has is the screen boot opened on, and a page that never got that
  // far (BOOT_DEADLINE_MS in App.tsx) is not a row at all. `first_visit` is a
  // first touch written by this very load.
  track('app_opened', { route: window.location.pathname, first_visit: !!c?.at && c.at !== before });
}

/**
 * Which way in: the app's own front door, or somebody's shared card. This is
 * NOT the front-door variant — that is `entry`, and the two must not be
 * confused: `door` is already in PostHog and already in the funnel
 * definitions, and overloading it would rewrite history.
 */
export type Door = 'front' | 'side';

export interface EventBase {
  challengeId?: string; day?: number; door?: Door; path?: Path;
  /**
   * Which front door they came through (17 §6.3) — the slug from
   * `src/data/doors.ts`, set once on the first landing and carried by every
   * event afterwards. The whole point of the feature is one breakdown:
   * arrival → door_played → intro_done → first card → D2 → D7, split by this.
   */
  entry?: string;
  /**
   * Which first activity after the intro (`?start=imagine`, lib/starter.ts,
   * well/product/33). Absent is the talk warm-up. The funnel that matters is
   * intro_done → first card, split by this.
   */
  starter?: string;
  // Where they came from, captured on the first landing and kept. On every
  // event, because the question is never "did the ad get a click", it is
  // "did the ad get somebody who made something and came back".
  source?: string; medium?: string; campaign?: string; content?: string;
  /**
   * The idea-step experiment’s arm (lib/experiments.ts, well/product/33
   * §8), on every event so any funnel can be broken down by it.
   */
  idea_step?: 'on' | 'off';
  /** The place-step experiment’s arm (well/product/33 §9): the Postcard Machine or the box. */
  place_step?: 'machine' | 'box';
  /** The landing experiment’s arm (well/product/33 §10): the first question, or the road. Ad visitors only. */
  landing?: 'quiz' | 'road';
}

let base: EventBase = {};
export function setEventBase(b: Partial<EventBase>) { base = { ...base, ...b }; }

export function track(event: string, props: Record<string, unknown> = {}) {
  const payload = { ...base, ...props, app: 'kindling' };
  if (import.meta.env.DEV) console.debug('[kindling]', event, payload);
  metaFromEvent(event);
  if (!ready) return;
  posthog.capture(event, payload);
}
